import { useCallback, useMemo } from 'react';
import { useDailyState } from '../../lib/storage';
import {
  clearCompleted,
  createId,
  createTodo,
  isUnknownArray,
  migrateTodos,
  removeTodo,
  todoCounts,
  toggleTodo,
  type Todo,
  type TodoCounts,
} from '../../lib/dailies';

const NO_ITEMS: readonly unknown[] = [];

export interface TodosState {
  readonly items: readonly Todo[];
  readonly counts: TodoCounts;
  readonly add: (text: string) => void;
  readonly toggle: (id: string) => void;
  readonly remove: (id: string) => void;
  readonly clearDone: () => void;
}

/**
 * `my-hub:todos:items` on `useDailyState` — the whole list is today's list and
 * is reset by the shared midnight ticker, live, with the tab still open.
 *
 * This hook deliberately touches NOTHING in the `top` storage area. The top
 * bar's One Priority (`my-hub:top:priority`) is a separate field with a
 * separate owner; the spec calls this list "separate from the One Priority",
 * so there is no shared key and no shared state between them.
 */
export function useTodos(): TodosState {
  const [raw, setRaw] = useDailyState<readonly unknown[]>(
    'todos',
    'items',
    NO_ITEMS,
    isUnknownArray,
  );

  const items = useMemo(() => migrateTodos(raw), [raw]);
  const counts = useMemo(() => todoCounts(items), [items]);

  const add = useCallback(
    (text: string) => {
      const todo = createTodo(createId('todo'), text);
      if (todo.text === '') return;
      setRaw([...items, todo]);
    },
    [items, setRaw],
  );

  const toggle = useCallback((id: string) => setRaw(toggleTodo(items, id)), [items, setRaw]);
  const remove = useCallback((id: string) => setRaw(removeTodo(items, id)), [items, setRaw]);
  const clearDone = useCallback(() => setRaw(clearCompleted(items)), [items, setRaw]);

  return { items, counts, add, toggle, remove, clearDone };
}
